import {
    useCallback,
    useMemo,
    useState,
} from 'react'
import {
    noop,
} from 'lodash'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import BatchOperationsRequests from 'app/requests/BatchOperations/BatchOperations.requests'
import {
    BatchEntity,
} from 'app/enums/common.enums'
import {
    WithId,
} from 'app/types/common.types'

type Props = {
    targetEntity: BatchEntity,
}

const useBatchOperation = ({
    targetEntity,
}: Readonly<Props>) => {
    const [
        batchId,
        setBatchId,
    ] = useState<number | null>(null)

    const {
        mutateAsync: startBatchOperation,
    } = useSimpleApiRequest({
        name: 'StartBatchOperation',
        requestParams: BatchOperationsRequests.start(),
        onSuccess: (resp) => {
            setBatchId(resp.id)
        },
        title: 'Batch Operation Started',
    })

    const {
        mutate: cancelBatchOperation,
    } = useSimpleApiRequest({
        name: 'CancelBatchOperation',
        requestParams: BatchOperationsRequests.cancel({
            id: batchId,
        }),
        onSuccess: noop,
        title: 'Batch Operation Cancelled',
    })

    const {
        mutate: completeBatchOperation,
    } = useSimpleApiRequest({
        name: 'CompleteBatchOperation',
        requestParams: BatchOperationsRequests.complete({
            id: batchId,
        }),
        onSuccess: noop,
        title: 'Batch Operation Completed',
    })

    const handleStartBatchOperation = useCallback((data: { batchSize: number }) => {
        return startBatchOperation({
            targetEntity,
            ...data,
        }) as Promise<WithId<number>>
    }, [
        startBatchOperation,
        targetEntity,
    ])

    return useMemo(() => {
        return {
            startBatchOperation: handleStartBatchOperation,
            cancelBatchOperation,
            completeBatchOperation,
            batchId,
        }
    }, [
        batchId,
        cancelBatchOperation,
        completeBatchOperation,
        handleStartBatchOperation,
    ])
}

export default useBatchOperation
